import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getApiBaseUrl } from "../config/api";
import ContactSeo from "../components/ContactSeo";
import useReveal from "../hooks/useReveal";
import { getRecaptchaResponse, getRecaptchaSiteKey, loadRecaptchaScript, resetRecaptcha } from "../utils/recaptcha";
import { compactIntlPhone, formatIntlPhone } from "../utils/formatIntlPhone";
import { enquiryContextFromState, publicEnquiryUrl, WHATSAPP_ICON_PATH, whatsappEnquiryHref } from "../utils/whatsappEnquiry";
import contactHero from "../assets/contact-hero.jpg";
import contactQuickResponse from "../assets/contact-quick-response.png";

const PAGE_GUTTER = "px-16 md:px-32 lg:px-40";
const ENQUIRY_TYPES = [
  "General Enquiry",
  "Request a Quote",
  "Product Availability",
  "Delivery Enquiry",
  "Supplier Partnership",
  "Other",
];
const QUOTE_TYPE = "Request a Quote";
const GENERAL_TYPE = "General Enquiry";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LIMITS = {
  name: 100,
  company: 150,
  email: 254,
  phone: 30,
  location: 200,
  subject: 150,
  message: 3000,
};

const EMPTY_FORM = {
  name: "",
  company: "",
  email: "",
  phone: "",
  enquiryType: GENERAL_TYPE,
  location: "",
  subject: "",
  message: "",
  website: "",
};

const FIELD_ORDER = ["name", "company", "email", "phone", "enquiryType", "location", "subject", "message"];
const FIELD_IDS = {
  name: "enquiry-name",
  company: "enquiry-company",
  email: "enquiry-email",
  phone: "enquiry-phone",
  enquiryType: "enquiry-type",
  location: "enquiry-location",
  subject: "enquiry-subject",
  message: "enquiry-message",
};

function lockDoubleClick(event) {
  const el = event.currentTarget;
  if (el.dataset.navLock === "1") {
    event.preventDefault();
    return;
  }
  el.dataset.navLock = "1";
  window.setTimeout(() => {
    delete el.dataset.navLock;
  }, 700);
}

function cleanText(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "undefined" || text === "null") return "";
  return text;
}

function telHref(number) {
  const digits = String(number || "").replace(/\D/g, "");
  if (!digits) return "";
  return `tel:+${digits}`;
}

function externalHref(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw.replace(/^\/+/, "")}`;
}

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function scrollToForm() {
  const el = document.getElementById("contact-form") || document.getElementById("send-an-enquiry");
  if (!el) return;
  el.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
  window.setTimeout(
    () => {
      document.getElementById("enquiry-name")?.focus({ preventScroll: true });
    },
    prefersReducedMotion() ? 0 : 420
  );
}

function isQuoteIntent(search) {
  return new URLSearchParams(search || "").get("intent") === "quote";
}

function sourcePathFrom(routeLocation) {
  const fromState = enquiryContextFromState(routeLocation.state);
  if (fromState.path) return fromState.path;
  try {
    const referrer = document.referrer ? new URL(document.referrer) : null;
    if (referrer && referrer.origin === window.location.origin) {
      return referrer.pathname;
    }
  } catch {
    /* ignore invalid referrer */
  }
  return String(routeLocation.pathname || "").split(/[?#]/)[0] || "/contact";
}

function sourcePageFrom(routeLocation) {
  const path = sourcePathFrom(routeLocation);
  return publicEnquiryUrl(path) || path;
}

function validateField(key, rawValue) {
  const value = key === "phone" ? String(rawValue || "").trim() : cleanText(rawValue);
  if (key === "name") {
    if (!value) return "Enter your name.";
    if (value.length > LIMITS.name) return "Name must be 100 characters or fewer.";
    return "";
  }
  if (key === "company") {
    if (value.length > LIMITS.company) return "Company name must be 150 characters or fewer.";
    return "";
  }
  if (key === "email") {
    if (!value) return "Enter your email address.";
    if (value.length > LIMITS.email) return "Email must be 254 characters or fewer.";
    if (!EMAIL_RE.test(value)) return "Enter a valid email address.";
    return "";
  }
  if (key === "phone") {
    const digits = value.replace(/\D/g, "");
    if (!digits) return "Enter your phone or WhatsApp number.";
    if (value.length > LIMITS.phone) return "Phone number must be 30 characters or fewer.";
    return "";
  }
  if (key === "enquiryType") {
    if (!value) return "Select an enquiry type.";
    if (!ENQUIRY_TYPES.includes(value)) return "Select an enquiry type.";
    return "";
  }
  if (key === "location") {
    if (value.length > LIMITS.location) return "Location must be 200 characters or fewer.";
    return "";
  }
  if (key === "subject") {
    if (value.length > LIMITS.subject) return "Subject must be 150 characters or fewer.";
    return "";
  }
  if (key === "message") {
    if (!value) return "Enter your message.";
    if (value.length > LIMITS.message) return "Message must be 3000 characters or fewer.";
    return "";
  }
  return "";
}

function validateForm(form) {
  const next = {};
  FIELD_ORDER.forEach((key) => {
    const message = validateField(key, form[key]);
    if (message) next[key] = message;
  });
  return next;
}

function ContactIcon({ name, className }) {
  const isWhatsapp = name === "whatsapp";
  const common = {
    viewBox: "0 0 24 24",
    className: className || "h-5 w-5 shrink-0 text-at-gold",
    fill: isWhatsapp || name === "instagram" || name === "facebook" ? "currentColor" : "none",
    stroke: isWhatsapp || name === "instagram" || name === "facebook" ? "none" : "currentColor",
    strokeWidth: isWhatsapp || name === "instagram" || name === "facebook" ? undefined : "1.7",
    "aria-hidden": true,
  };
  if (name === "building") {
    return (
      <svg {...common}>
        <path d="M4 21V5l8-2 8 2v16" />
        <path d="M9 21v-6h6v6M9 8h.01M15 8h.01M9 12h.01M15 12h.01" />
      </svg>
    );
  }
  if (name === "phone") {
    return (
      <svg {...common}>
        <path d="M7 3h4l1 4-3 2a12 12 0 006 6l2-3 4 1v4c0 1-1 3-8 3C6 20 3 10 3 7c0-7 3-4 4-4z" />
      </svg>
    );
  }
  if (name === "mail") {
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="14" rx="1.5" />
        <path d="M4 7l8 6 8-6" />
      </svg>
    );
  }
  if (name === "whatsapp") {
    return (
      <svg {...common}>
        <path d={WHATSAPP_ICON_PATH} />
      </svg>
    );
  }
  if (name === "pin") {
    return (
      <svg {...common}>
        <path d="M12 21s7-5.3 7-11a7 7 0 10-14 0c0 5.7 7 11 7 11z" />
        <circle cx="12" cy="10" r="2" />
      </svg>
    );
  }
  if (name === "instagram") {
    return (
      <svg {...common}>
        <path d="M7 3h10a4 4 0 014 4v10a4 4 0 01-4 4H7a4 4 0 01-4-4V7a4 4 0 014-4zm10 1.8H7A2.2 2.2 0 004.8 7v10A2.2 2.2 0 007 19.2h10a2.2 2.2 0 002.2-2.2V7A2.2 2.2 0 0017 4.8zM12 8.2A3.8 3.8 0 1112 15.8 3.8 3.8 0 0112 8.2zm0 1.7a2.1 2.1 0 100 4.2 2.1 2.1 0 000-4.2zM17.35 6.4a1 1 0 110 2 1 1 0 010-2z" />
      </svg>
    );
  }
  if (name === "facebook") {
    return (
      <svg {...common}>
        <path d="M14.5 9H16V6.5h-1.5c-2 0-3.3 1.2-3.3 3.2V11H10v2.4h1.2V21h2.6v-7.6H16L16.5 11h-2.7V9.8c0-.5.3-.8.7-.8z" />
      </svg>
    );
  }
  if (name === "box") {
    return (
      <svg {...common} className={className || "h-8 w-8 text-at-gold"}>
        <path d="M3 8l9-4 9 4-9 4-9-4z" />
        <path d="M3 8v8l9 4 9-4V8" />
      </svg>
    );
  }
  if (name === "truck") {
    return (
      <svg {...common} className={className || "h-8 w-8 text-at-gold"}>
        <path d="M3 7h11v8H3z" />
        <path d="M14 10h4l3 3v2h-7" />
        <circle cx="7" cy="17" r="1.5" />
        <circle cx="18" cy="17" r="1.5" />
      </svg>
    );
  }
  return (
    <svg {...common} className={className || "h-8 w-8 text-at-gold"}>
      <circle cx="9" cy="8" r="3" />
      <circle cx="16" cy="9" r="2.4" />
      <path d="M4 19c1-3.2 3.2-5 5-5s4 1.8 5 5M14 19c.4-2 1.6-3.4 3.2-3.8" />
    </svg>
  );
}

function ContactIconCircle({ name }) {
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-100">
      <ContactIcon name={name} className="h-4 w-4 text-at-dark" />
    </span>
  );
}

function ContactChevron() {
  return (
    <svg viewBox="0 0 24 24" className="contact-row-arrow h-4 w-4 shrink-0 text-neutral-300" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

const CONTACT_LI = "border-b border-neutral-200 last:border-b-0";
const CONTACT_ROW = "contact-row flex items-center gap-3 py-5";

function ContactRow({ icon, label, children, href, external, showArrow, ariaLabel }) {
  if (!children) return null;
  const arrow = showArrow ?? Boolean(href);
  const body = (
    <>
      <ContactIconCircle name={icon} />
      <span className="min-w-0 flex-1">
        {label ? <span className="block text-xs font-extrabold uppercase tracking-[0.14em] text-at-dark">{label}</span> : null}
        <span className="mt-0.5 block text-sm text-at-dark">{children}</span>
      </span>
      {arrow ? <ContactChevron /> : null}
    </>
  );
  if (href) {
    return (
      <li className={CONTACT_LI}>
        <a
          href={href}
          target={external ? "_blank" : undefined}
          rel={external ? "noopener noreferrer" : undefined}
          aria-label={ariaLabel}
          className={`${CONTACT_ROW} hover:text-at-dark`}
        >
          {body}
        </a>
      </li>
    );
  }
  return (
    <li className={CONTACT_LI}>
      <div className={CONTACT_ROW}>{body}</div>
    </li>
  );
}

function Field({ id, label, required, error, children }) {
  const errorId = `${id}-error`;
  return (
    <label className="block" htmlFor={id}>
      <span className="text-xs font-medium text-neutral-500">
        {label}
        {required ? <span className="text-at-gold"> *</span> : null}
      </span>
      <span className="mt-0.5 block">{children}</span>
      <span id={errorId} className="contact-field-error mt-0.5 block text-xs text-at-dark">
        {error || ""}
      </span>
    </label>
  );
}

function ContactPage() {
  const routeLocation = useLocation();
  const navigate = useNavigate();
  const pageRef = useRef(null);
  const quoteAppliedRef = useRef(false);
  const submitLockRef = useRef(false);
  const [contact, setContact] = useState(null);
  const [contactStatus, setContactStatus] = useState("loading");
  const [form, setForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [liveMessage, setLiveMessage] = useState("");
  const dialogMessageRef = useRef(null);
  const recaptchaHostRef = useRef(null);
  const recaptchaWidgetIdRef = useRef(null);
  const recaptchaSiteKey = getRecaptchaSiteKey();

  useReveal(pageRef);

  useEffect(() => {
    if (!recaptchaSiteKey) return undefined;
    let cancelled = false;
    let widgetId = null;

    loadRecaptchaScript()
      .then((grecaptcha) => {
        const host = recaptchaHostRef.current;
        if (cancelled || !host || widgetId != null) return;
        widgetId = grecaptcha.render(host, {
          sitekey: recaptchaSiteKey,
          callback: () => {
            setErrors((prev) => {
              if (!prev.recaptcha) return prev;
              const next = { ...prev };
              delete next.recaptcha;
              return next;
            });
          },
          "expired-callback": () => {
            setErrors((prev) => ({ ...prev, recaptcha: "Please confirm you are not a robot." }));
          },
        });
        recaptchaWidgetIdRef.current = widgetId;
      })
      .catch(() => {
        if (!cancelled) {
          setErrors((prev) => ({ ...prev, recaptcha: "Verification could not load. Please refresh the page." }));
        }
      });

    return () => {
      cancelled = true;
      recaptchaWidgetIdRef.current = null;
      if (widgetId != null) {
        resetRecaptcha(widgetId);
      }
    };
  }, [recaptchaSiteKey]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (!cancelled) {
          setContact(Array.isArray(list) && list[0] ? list[0] : null);
          setContactStatus("ready");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setContact(null);
          setContactStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const state = routeLocation.state;
    if (!state) return;
    const productName = cleanText(state.productName);
    const deliveryTitle = cleanText(state.deliveryTitle);
    const categoryName = cleanText(state.categoryName);
    const productNames = Array.isArray(state.productNames) ? state.productNames.filter(Boolean).join(", ") : "";
    const deliveryLocation = cleanText(state.deliveryLocation);
    if (!productName && !deliveryTitle && !categoryName) return;
    setForm((prev) => ({
      ...prev,
      subject: prev.subject || (productName ? `Enquiry about ${productName}` : deliveryTitle || `Enquiry about ${categoryName}`),
      message:
        prev.message ||
        (productName
          ? `I would like to enquire about ${productName}.`
          : deliveryTitle
            ? `I would like to enquire about ${deliveryTitle}${productNames ? ` (${productNames})` : ""}${deliveryLocation ? ` for delivery to ${deliveryLocation}` : ""}.`
            : `I would like to enquire about ${categoryName}.`),
    }));
  }, [routeLocation.state]);

  const quoteIntent = isQuoteIntent(routeLocation.search);

  useEffect(() => {
    if (!quoteIntent) {
      quoteAppliedRef.current = false;
      return;
    }
    if (quoteAppliedRef.current) return;
    quoteAppliedRef.current = true;
    setForm((prev) => ({ ...prev, enquiryType: QUOTE_TYPE }));
  }, [quoteIntent]);

  const siteName = cleanText(contact?.siteName);
  const companyRegNo = cleanText(contact?.companyRegNo);
  const emailAddress = cleanText(contact?.emailAddress);
  const whatsappNumber = cleanText(contact?.whatsappNumber);
  const instagramHref = externalHref(contact?.instagram);
  const facebookHref = externalHref(contact?.facebook);
  const phoneDisplay = formatIntlPhone(whatsappNumber);
  const phoneHref = telHref(whatsappNumber);
  const fromState = enquiryContextFromState(routeLocation.state);
  const sourcePath = sourcePathFrom(routeLocation);
  const waHref = whatsappEnquiryHref({
    number: whatsappNumber,
    siteName,
    title: fromState.title,
    path: sourcePath === "/contact" ? "" : sourcePath,
  });

  useEffect(() => {
    const hash = routeLocation.hash;
    if (hash === "#contact-form" || hash === "#send-an-enquiry" || quoteIntent) {
      const timer = window.setTimeout(scrollToForm, 80);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [routeLocation.hash, routeLocation.search, quoteIntent, contact]);

  useEffect(() => {
    if (!dialog) return undefined;
    dialogMessageRef.current?.focus();
    function onKey(event) {
      if (event.key === "Escape") setDialog(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dialog]);

  const visibleErrors = useMemo(() => {
    const next = {};
    FIELD_ORDER.forEach((key) => {
      if (touched[key] && errors[key]) next[key] = errors[key];
    });
    if (errors.recaptcha) next.recaptcha = errors.recaptcha;
    return next;
  }, [touched, errors]);

  const errorSummary = Object.values(visibleErrors).join(" ");

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!touched[key]) return prev;
      const message = validateField(key, value);
      if (!message) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: message };
    });
  }

  function onBlurField(key) {
    setTouched((prev) => ({ ...prev, [key]: true }));
    const message = validateField(key, form[key]);
    setErrors((prev) => {
      if (!message) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: message };
    });
  }

  function goToQuoteForm(event) {
    event.preventDefault();
    lockDoubleClick(event);
    navigate({ pathname: "/contact", search: "?intent=quote", hash: "#contact-form" }, { replace: true, state: routeLocation.state });
  }

  async function onSubmit(event) {
    event.preventDefault();
    if (submitting || submitLockRef.current) return;
    const nextErrors = validateForm(form);
    const allTouched = {};
    FIELD_ORDER.forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      const firstKey = FIELD_ORDER.find((key) => nextErrors[key]);
      const firstId = FIELD_IDS[firstKey];
      document.getElementById(firstId)?.focus();
      setLiveMessage(`Please correct the highlighted fields. ${Object.values(nextErrors).join(" ")}`);
      return;
    }
    const recaptchaToken = getRecaptchaResponse(recaptchaWidgetIdRef.current);
    if (!recaptchaSiteKey || !recaptchaToken) {
      const recaptchaError = recaptchaSiteKey
        ? "Please confirm you are not a robot."
        : "Verification is unavailable. Please try again later.";
      setErrors((prev) => ({ ...prev, recaptcha: recaptchaError }));
      setLiveMessage(recaptchaError);
      recaptchaHostRef.current?.scrollIntoView({ block: "center" });
      return;
    }
    submitLockRef.current = true;
    setSubmitting(true);
    setLiveMessage("Submitting your enquiry.");
    try {
      const res = await fetch(`${getApiBaseUrl()}/forms`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          name: cleanText(form.name),
          companyName: cleanText(form.company),
          email: cleanText(form.email),
          phone: compactIntlPhone(form.phone),
          enquiry: cleanText(form.enquiryType),
          location: cleanText(form.location),
          subject: cleanText(form.subject),
          message: cleanText(form.message),
          sourcePage: sourcePageFrom(routeLocation),
          website: form.website,
          recaptchaToken,
        }),
      });
      if (!res.ok) {
        resetRecaptcha(recaptchaWidgetIdRef.current);
        setDialog({
          kind: "error",
          message: "We could not send your enquiry just now. Please try again.",
        });
        setLiveMessage("We could not send your enquiry just now. Please try again.");
        return;
      }
      resetRecaptcha(recaptchaWidgetIdRef.current);
      setForm({
        ...EMPTY_FORM,
        enquiryType: quoteIntent ? QUOTE_TYPE : GENERAL_TYPE,
      });
      setTouched({});
      setErrors({});
      setDialog({
        kind: "success",
        message: "Thank you. Your enquiry has been received. We will get back to you as soon as possible.",
      });
      setLiveMessage("Thank you. Your enquiry has been received. We will get back to you as soon as possible.");
    } catch {
      resetRecaptcha(recaptchaWidgetIdRef.current);
      setDialog({
        kind: "error",
        message: "We could not send your enquiry just now. Please try again.",
      });
      setLiveMessage("We could not send your enquiry just now. Please try again.");
    } finally {
      setSubmitting(false);
      submitLockRef.current = false;
    }
  }

  const inputClass =
    "contact-field w-full rounded-[2px] border border-neutral-200 bg-white px-3 py-2.5 text-sm text-at-dark outline-none placeholder:text-neutral-400 focus:border-at-gold";

  return (
    <div className="contact-page overflow-x-hidden" ref={pageRef}>
      <ContactSeo contact={contact} contactStatus={contactStatus} />
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {liveMessage}
      </div>
      <section className="relative overflow-hidden bg-at-dark text-white">
        <img
          src={contactHero}
          alt=""
          width={1400}
          height={467}
          fetchPriority="high"
          className="hero-zoom-subtle pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="pointer-events-none absolute inset-0 bg-black/20" />
        <div className="pointer-events-none absolute inset-y-0 left-0 hidden h-full w-[58%] bg-gradient-to-r from-black/75 via-black/40 to-transparent md:block" />
        <div className={`relative z-10 flex min-h-[320px] flex-col justify-between py-10 md:min-h-[420px] md:py-12 ${PAGE_GUTTER}`}>
          <nav aria-label="Breadcrumb" className="hero-in text-xs text-white/70">
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <span className="px-2 text-white/40">/</span>
            <span className="text-white">Contact</span>
          </nav>
          <div className="mb-6 max-w-xl md:mb-10">
            <p className="hero-in hero-in-1 mt-8 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold">
              <span className="inline-block h-1 w-12 bg-at-gold" aria-hidden="true" />
              Let&apos;s Work Together
            </p>
            <h1 className="hero-in hero-in-2 mt-2 font-serif text-3xl uppercase leading-tight md:text-5xl">
              Contact Our
              <br />
              Supply Team
            </h1>
            <p className="hero-in hero-in-3 mt-3 max-w-md text-sm leading-relaxed text-white/70">
              Tell us what materials you need and our team will assist with sourcing, availability and delivery.
            </p>
            <div className="hero-in hero-in-4 mt-6 flex flex-wrap gap-3">
              <Link
                to="/contact?intent=quote#contact-form"
                replace
                state={routeLocation.state}
                onClick={goToQuoteForm}
                className="hero-btn rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
              >
                Request a Quote <span className="btn-arrow">→</span>
              </Link>
              {waHref ? (
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={lockDoubleClick}
                  className="hero-btn inline-flex items-center gap-2 rounded-sm border border-white/70 bg-black/40 px-5 py-2.5 text-sm font-semibold text-white hover:bg-black/60"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                    <path d={WHATSAPP_ICON_PATH} />
                  </svg>
                  WhatsApp Us
                </a>
              ) : (
                <Link
                  to="/contact?intent=quote#contact-form"
                  replace
                  state={routeLocation.state}
                  onClick={goToQuoteForm}
                  className="hero-btn rounded-sm border border-white/70 bg-black/40 px-5 py-2.5 text-sm font-semibold text-white hover:bg-black/60"
                >
                  WhatsApp Us
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="min-w-0 overflow-x-hidden bg-white px-5 py-14 md:px-32 lg:px-40">
        <div className="grid min-w-0 items-start gap-12 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-16">
          <div data-reveal="" className="min-w-0 text-center lg:text-left">
            <h2 className="font-serif text-4xl uppercase leading-tight text-at-dark md:text-5xl">Get In Touch</h2>
            <p className="mt-3 text-lg text-at-dark md:text-xl">Talk to Our Team</p>
            <span className="mx-auto mt-2 block h-0.5 w-10 bg-at-gold lg:mx-0" aria-hidden="true" />
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-neutral-500 lg:mx-0">
              We&apos;re ready to help with your sourcing, stock availability and delivery across Malaysia.
            </p>
            <address className="mx-auto mt-8 max-w-md not-italic text-left lg:mx-0">
            <ul>
              {siteName || companyRegNo ? (
                <li className={CONTACT_LI}>
                  <div className={CONTACT_ROW}>
                    <ContactIconCircle name="building" />
                    <span className="min-w-0 flex-1">
                      {siteName ? <span className="block text-sm font-semibold text-at-dark">{siteName}</span> : null}
                      {companyRegNo ? (
                        <span className="mt-1 block text-xs text-neutral-500">Company Registration No. {companyRegNo}</span>
                      ) : null}
                    </span>
                  </div>
                </li>
              ) : null}
              <ContactRow icon="phone" label="Phone" href={phoneHref} ariaLabel={phoneDisplay ? `Call ${phoneDisplay}` : "Call"}>
                {phoneDisplay}
              </ContactRow>
              <ContactRow icon="mail" label="Email" href={emailAddress ? `mailto:${emailAddress}` : ""} ariaLabel={emailAddress ? `Email ${emailAddress}` : "Email"}>
                {emailAddress}
              </ContactRow>
              <ContactRow icon="whatsapp" label="WhatsApp" href={waHref} external ariaLabel={phoneDisplay ? `Chat on WhatsApp ${phoneDisplay}` : "Chat on WhatsApp"}>
                {phoneDisplay}
              </ContactRow>
              <ContactRow icon="pin" label="Service Area" showArrow>
                Based in Klang Valley, supplying across Malaysia
              </ContactRow>
              <ContactRow icon="instagram" label="Instagram" href={instagramHref} external ariaLabel="Visit Instagram">
                {instagramHref ? "Instagram" : ""}
              </ContactRow>
              <ContactRow icon="facebook" label="Facebook" href={facebookHref} external ariaLabel="Visit Facebook">
                {facebookHref ? "Facebook" : ""}
              </ContactRow>
            </ul>
            </address>

            <div className="contact-quick-card relative mt-8 overflow-hidden p-6 pb-16 text-white md:p-8 md:pb-20">
              <img
                src={contactQuickResponse}
                alt=""
                loading="lazy"
                decoding="async"
                className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
              />
              <div className="pointer-events-none absolute inset-0 bg-black/85" />
              <div className="relative z-10 flex flex-col items-center text-center lg:items-start lg:text-left">
                <p className="flex items-center justify-center gap-2 text-sm font-semibold lg:justify-start">
                  <ContactIcon name="whatsapp" className="h-5 w-5 text-white" />
                  Need a Quick Response?
                </p>
                <p className="mt-2 text-sm text-white/70">Send us your requirements directly through WhatsApp.</p>
                {waHref ? (
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={lockDoubleClick}
                    className="hero-btn mt-8 inline-flex items-center gap-2 rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
                  >
                    Chat on WhatsApp <span className="btn-arrow">→</span>
                  </a>
                ) : (
                  <a
                    href="#contact-form"
                    onClick={(event) => {
                      event.preventDefault();
                      goToQuoteForm(event);
                    }}
                    className="hero-btn mt-8 inline-flex items-center gap-2 rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
                  >
                    Send an Enquiry <span className="btn-arrow">→</span>
                  </a>
                )}
              </div>
            </div>
          </div>

          <div
            id="send-an-enquiry"
            className="min-w-0 scroll-mt-28 rounded-[2px] border border-at-gold bg-white p-6 md:p-8"
            tabIndex={-1}
            data-reveal=""
          >
            <p className="text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold lg:text-left">Send an Enquiry</p>
            <h2 className="mt-2 text-center font-serif text-3xl uppercase text-at-dark md:text-4xl lg:text-left">How Can We Help?</h2>
            <p className="mt-2 text-center text-sm text-neutral-500 lg:text-left">Complete the form and our team will get back to you.</p>
            <form id="contact-form" className="mt-8 scroll-mt-28 text-left" onSubmit={onSubmit} noValidate aria-busy={submitting}>
              <div className="sr-only" aria-live="assertive">
                {errorSummary ? `Please correct the following. ${errorSummary}` : ""}
              </div>
              <div hidden aria-hidden="true">
                <label htmlFor="enquiry-website">Website</label>
                <input
                  id="enquiry-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(event) => setField("website", event.target.value)}
                />
              </div>
              <div className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field id="enquiry-name" label="Name" required error={visibleErrors.name}>
                  <input
                    id="enquiry-name"
                    name="name"
                    autoComplete="name"
                    required
                    maxLength={LIMITS.name}
                    aria-required="true"
                    aria-invalid={Boolean(visibleErrors.name)}
                    aria-describedby="enquiry-name-error"
                    value={form.name}
                    onChange={(event) => setField("name", event.target.value)}
                    onBlur={() => onBlurField("name")}
                    placeholder="Your full name"
                    className={inputClass}
                  />
                </Field>
                <Field id="enquiry-company" label="Company Name" error={visibleErrors.company}>
                  <input
                    id="enquiry-company"
                    name="company"
                    autoComplete="organization"
                    maxLength={LIMITS.company}
                    aria-invalid={Boolean(visibleErrors.company)}
                    aria-describedby="enquiry-company-error"
                    value={form.company}
                    onChange={(event) => setField("company", event.target.value)}
                    onBlur={() => onBlurField("company")}
                    placeholder="Your company name"
                    className={inputClass}
                  />
                </Field>
                <Field id="enquiry-email" label="Email" required error={visibleErrors.email}>
                  <input
                    id="enquiry-email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    maxLength={LIMITS.email}
                    aria-required="true"
                    aria-invalid={Boolean(visibleErrors.email)}
                    aria-describedby="enquiry-email-error"
                    value={form.email}
                    onChange={(event) => setField("email", event.target.value)}
                    onBlur={() => onBlurField("email")}
                    placeholder="you@example.com"
                    className={inputClass}
                  />
                </Field>
                <Field id="enquiry-phone" label="Phone / WhatsApp" required error={visibleErrors.phone}>
                  <input
                    id="enquiry-phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    required
                    aria-required="true"
                    aria-invalid={Boolean(visibleErrors.phone)}
                    aria-describedby="enquiry-phone-error"
                    value={form.phone}
                    onChange={(event) => setField("phone", formatIntlPhone(event.target.value))}
                    onBlur={() => onBlurField("phone")}
                    placeholder="60 12 345 6789"
                    className={inputClass}
                  />
                </Field>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
              <Field id="enquiry-type" label="Enquiry Type" required error={visibleErrors.enquiryType}>
                <select
                  id="enquiry-type"
                  name="enquiryType"
                  required
                  aria-required="true"
                  aria-invalid={Boolean(visibleErrors.enquiryType)}
                  aria-describedby="enquiry-type-error"
                  value={form.enquiryType}
                  onChange={(event) => setField("enquiryType", event.target.value)}
                  onBlur={() => onBlurField("enquiryType")}
                  className={`${inputClass} bg-white`}
                >
                  <option value="">Select enquiry type</option>
                  {ENQUIRY_TYPES.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </Field>
              <Field id="enquiry-location" label="Location / Country" error={visibleErrors.location}>
                <input
                  id="enquiry-location"
                  name="location"
                  autoComplete="country-name"
                  maxLength={LIMITS.location}
                  aria-invalid={Boolean(visibleErrors.location)}
                  aria-describedby="enquiry-location-error"
                  value={form.location}
                  onChange={(event) => setField("location", event.target.value)}
                  onBlur={() => onBlurField("location")}
                  placeholder="e.g. Selangor, Malaysia"
                  className={inputClass}
                />
              </Field>
              </div>
              <Field id="enquiry-subject" label="Subject" error={visibleErrors.subject}>
                <input
                  id="enquiry-subject"
                  name="subject"
                  autoComplete="off"
                  maxLength={LIMITS.subject}
                  aria-invalid={Boolean(visibleErrors.subject)}
                  aria-describedby="enquiry-subject-error"
                  value={form.subject}
                  onChange={(event) => setField("subject", event.target.value)}
                  onBlur={() => onBlurField("subject")}
                  placeholder="Brief subject"
                  className={inputClass}
                />
              </Field>
              <Field id="enquiry-message" label="Message" required error={visibleErrors.message}>
                <textarea
                  id="enquiry-message"
                  name="message"
                  required
                  rows={4}
                  maxLength={LIMITS.message}
                  aria-required="true"
                  aria-invalid={Boolean(visibleErrors.message)}
                  aria-describedby="enquiry-message-error"
                  value={form.message}
                  onChange={(event) => setField("message", event.target.value)}
                  onBlur={() => onBlurField("message")}
                  placeholder="Tell us what products, quantities or assistance you need."
                  className={`${inputClass} resize-y`}
                />
              </Field>
              <div className="block">
                <span className="text-xs font-medium text-neutral-500">
                  Verification<span className="text-at-gold"> *</span>
                </span>
                <div className="mt-1 max-w-full overflow-x-auto">
                  <div ref={recaptchaHostRef} id="contact-recaptcha" className="w-max max-w-none" />
                  {!recaptchaSiteKey ? (
                    <p className="text-xs text-neutral-500">
                      Set REACT_APP_RECAPTCHA_SITE_KEY in frontend/.env and restart the frontend.
                    </p>
                  ) : null}
                </div>
                <span id="enquiry-recaptcha-error" className="contact-field-error mt-0.5 block text-xs text-at-dark">
                  {visibleErrors.recaptcha || ""}
                </span>
              </div>
              <button
                type="submit"
                disabled={submitting}
                onClick={lockDoubleClick}
                className="hero-btn inline-flex w-full items-center justify-center gap-2 rounded-[2px] bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? <span className="contact-submit-spinner" aria-hidden="true" /> : null}
                {submitting ? "Submitting…" : "Submit Enquiry"}
                {submitting ? null : <span className="btn-arrow">→</span>}
              </button>
              <p className="text-center text-xs text-neutral-400">We normally respond within one business day.</p>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="bg-at-paper">
        <ul className={`flex flex-col gap-6 py-10 md:flex-row md:items-center md:justify-between md:gap-0 ${PAGE_GUTTER}`}>
          <li className="contact-trust-item flex items-center gap-4" data-reveal="">
            <ContactIcon name="box" className="h-8 w-8 shrink-0 text-at-gold" />
            <div>
              <h3 className="text-sm font-semibold text-at-dark">Product Sourcing</h3>
              <p className="mt-0.5 text-xs text-neutral-500">Steel, hardware and industrial supplies</p>
            </div>
          </li>
          <li className="hidden px-6 text-lg text-neutral-300 md:block" aria-hidden="true">
            |
          </li>
          <li className="contact-trust-item flex items-center gap-4" data-reveal="" data-reveal-delay="1">
            <ContactIcon name="truck" className="h-8 w-8 shrink-0 text-at-gold" />
            <div>
              <h3 className="text-sm font-semibold text-at-dark">Malaysia-Wide Supply</h3>
              <p className="mt-0.5 text-xs text-neutral-500">Based in Klang Valley, supplying nationwide</p>
            </div>
          </li>
          <li className="hidden px-6 text-lg text-neutral-300 md:block" aria-hidden="true">
            |
          </li>
          <li className="contact-trust-item flex items-center gap-4" data-reveal="" data-reveal-delay="2">
            <ContactIcon name="users" className="h-8 w-8 shrink-0 text-at-gold" />
            <div>
              <h3 className="text-sm font-semibold text-at-dark">Direct Support</h3>
              <p className="mt-0.5 text-xs text-neutral-500">Speak to our sourcing team</p>
            </div>
          </li>
        </ul>
      </section>

      {dialog ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setDialog(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="enquiry-dialog-title"
            className="w-full max-w-md rounded-[2px] border border-at-gold bg-white p-6 text-center md:p-8"
            onClick={(event) => event.stopPropagation()}
          >
            <p
              id="enquiry-dialog-title"
              ref={dialogMessageRef}
              tabIndex={-1}
              className="text-sm leading-relaxed text-at-dark outline-none md:text-base"
            >
              {dialog.message}
            </p>
            <button
              type="button"
              onClick={() => setDialog(null)}
              className="hero-btn mt-6 inline-flex items-center justify-center rounded-[2px] bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ContactPage;
